// Season CRUD + context + participant curation against the backend
// `/admin/seasons` endpoints (issue #41 redesign).
//
// Every block carries an inline typed config; the map* helpers translate the
// builder's local draft graph (client-local string ids wired across the tree)
// to the backend request shape and back. On create/update the backend remaps
// client-local ids to real row uuids (including condition target_block_id
// references), so we send our local ids as-is.

import { api } from '@/core/lib/api'
import type {
  TSeason,
  TSeasonAdminApplications,
  TSeasonApplicationConfig,
  TSeasonApplicationRuleDefinition,
  TSeasonBlock,
  TSeasonCondition,
  TSeasonContext,
  TSeasonDraft,
  TSeasonEligibleCount,
  TSeasonEligibleCountQuery,
  TSeasonIntakeMode,
  TSeasonLeagueConfig,
  TSeasonListItem,
  TSeasonMatchupConfig,
  TSeasonNotice,
  TSeasonParticipant,
  TSeasonPeriod,
  TSeasonStatus,
  TSeasonTeamStatsResponse,
  TSeasonTournamentConfig,
  TTeamSeasonMembership,
} from '@/modules/season/season.types'

const base = '/admin/seasons'

// ---- backend wire shapes ---------------------------------------------------

type TConditionWire = {
  id?: string | null
  selector: TSeasonCondition['selector']
  action: TSeasonCondition['action']
  count: number | null
  range_start: number | null
  range_end: number | null
  target_block_id: string | null
  to_next_season: boolean
}

type TBlockWire = {
  id?: string | null
  kind: TSeasonBlock['kind']
  label: string
  intake_capacity: number | null
  instantiated_league_id?: string | null
  instantiated_tournament_id?: string | null
  conditions: TConditionWire[]
  league_config?: TSeasonLeagueConfig | null
  tournament_config?: TSeasonTournamentConfig | null
  matchup_config?: TSeasonMatchupConfig | null
  application_config?: TSeasonApplicationConfig | null
}

type TPeriodWire = {
  id?: string | null
  name: string
  start_date: string | null
  end_date: string | null
  status?: string
  order_index?: number
  blocks: TBlockWire[]
}

type TSeasonReadWire = {
  id: string
  name: string
  display_name: string
  season_number: number
  description: string | null
  status: TSeasonStatus
  team_tag_ids: string[] | null
  min_team_size: number | null
  max_team_size: number | null
  target_team_count: number | null
  intake_mode: TSeasonIntakeMode
  settings: { reset_teams_next_season: boolean; loop_enabled: boolean }
  periods: (TPeriodWire & { id: string })[]
  participants: TSeasonParticipant[] | null
  notices: TSeasonNotice[] | null
  created_at: string
  updated_at: string | null
}

// ---- dashboard -> backend --------------------------------------------------

function conditionToWire(c: TSeasonCondition): TConditionWire {
  return {
    id: c.id,
    selector: c.selector,
    action: c.action,
    count: c.count,
    range_start: c.range_start,
    range_end: c.range_end,
    target_block_id: c.target_block_id,
    to_next_season: c.to_next_season,
  }
}

function blockToWire(b: TSeasonBlock): TBlockWire {
  return {
    id: b.id,
    kind: b.kind,
    label: b.label,
    intake_capacity: b.intake_capacity,
    conditions: b.conditions.map(conditionToWire),
    // Send only the config matching the block kind.
    league_config: b.kind === 'league' ? b.league_config : undefined,
    tournament_config:
      b.kind === 'tournament' ? b.tournament_config : undefined,
    matchup_config: b.kind === 'matchup' ? b.matchup_config : undefined,
    application_config:
      b.kind === 'application' ? b.application_config : undefined,
  }
}

function draftToWire(draft: TSeasonDraft) {
  return {
    name: draft.name,
    description: draft.description ?? null,
    team_tag_ids: draft.team_tag_ids,
    min_team_size: draft.min_team_size,
    max_team_size: draft.max_team_size,
    target_team_count: draft.target_team_count,
    intake_mode: draft.intake_mode,
    explicit_team_ids: draft.explicit_team_ids,
    settings: draft.settings,
    periods: draft.periods.map((p) => ({
      id: p.id,
      name: p.name,
      start_date: p.start_date,
      end_date: p.end_date,
      blocks: p.blocks.map(blockToWire),
    })),
  }
}

// ---- backend -> dashboard --------------------------------------------------

function wireToBlock(b: TBlockWire & { id: string }): TSeasonBlock {
  const leagueConfig = b.league_config
    ? {
        ...b.league_config,
        group_count: b.league_config.group_count ?? null,
        group_size: b.league_config.group_size ?? null,
        active_hours_start: b.league_config.active_hours_start ?? null,
        active_hours_end: b.league_config.active_hours_end ?? null,
        custom_game_mode_id: b.league_config.custom_game_mode_id ?? null,
        game_modes: b.league_config.game_modes ?? [],
      }
    : null
  const tournamentConfig = b.tournament_config
    ? {
        ...b.tournament_config,
        sudden_death: b.tournament_config.sudden_death ?? null,
        active_hours_start: b.tournament_config.active_hours_start ?? null,
        active_hours_end: b.tournament_config.active_hours_end ?? null,
        custom_game_mode_id: b.tournament_config.custom_game_mode_id ?? null,
        game_modes: b.tournament_config.game_modes ?? [],
      }
    : null
  const matchupConfig = b.matchup_config
    ? {
        ...b.matchup_config,
        sudden_death: b.matchup_config.sudden_death ?? null,
        active_hours_start: b.matchup_config.active_hours_start ?? null,
        active_hours_end: b.matchup_config.active_hours_end ?? null,
        custom_game_mode_id: b.matchup_config.custom_game_mode_id ?? null,
        game_modes: b.matchup_config.game_modes ?? [],
      }
    : null

  return {
    id: b.id,
    kind: b.kind,
    label: b.label,
    intake_capacity: b.intake_capacity ?? null,
    instantiated_league_id: b.instantiated_league_id ?? null,
    instantiated_tournament_id: b.instantiated_tournament_id ?? null,
    conditions: (b.conditions ?? []).map((c) => ({
      id: c.id ?? '',
      selector: c.selector,
      action: c.action ?? 'advance',
      count: c.count ?? null,
      range_start: c.range_start ?? null,
      range_end: c.range_end ?? null,
      target_block_id: c.target_block_id ?? null,
      to_next_season: c.to_next_season ?? false,
    })),
    league_config: leagueConfig,
    tournament_config: tournamentConfig,
    matchup_config: matchupConfig,
    application_config: b.application_config
      ? {
          ...b.application_config,
          target_block_id: b.application_config.target_block_id ?? null,
          rules: b.application_config.rules ?? [],
        }
      : null,
  }
}

function wireToSeason(s: TSeasonReadWire): TSeason {
  const periods: TSeasonPeriod[] = s.periods.map((p) => ({
    id: p.id,
    name: p.name,
    start_date: p.start_date,
    end_date: p.end_date,
    status: p.status as TSeasonPeriod['status'],
    order_index: p.order_index,
    blocks: (p.blocks ?? []).map((b) =>
      wireToBlock(b as TBlockWire & { id: string })
    ),
  }))
  return {
    id: s.id,
    name: s.name,
    display_name: s.display_name,
    season_number: s.season_number,
    description: s.description,
    status: s.status,
    team_tag_ids: s.team_tag_ids ?? [],
    min_team_size: s.min_team_size ?? null,
    max_team_size: s.max_team_size ?? null,
    target_team_count: s.target_team_count ?? null,
    intake_mode: s.intake_mode ?? 'tag',
    settings: s.settings,
    periods,
    participants: s.participants ?? [],
    notices: s.notices ?? [],
    created_at: s.created_at,
    updated_at: s.updated_at ?? s.created_at,
  }
}

// ---- public API ------------------------------------------------------------

export async function listSeasons(): Promise<TSeasonListItem[]> {
  const res = await api.get<TSeasonListItem[]>(base)
  return res.data
}

export async function getSeason(id: string): Promise<TSeason> {
  const res = await api.get<TSeasonReadWire>(`${base}/${id}`)
  return wireToSeason(res.data)
}

export async function createSeason(draft: TSeasonDraft): Promise<TSeason> {
  const res = await api.post<TSeasonReadWire>(base, draftToWire(draft))
  return wireToSeason(res.data)
}

export async function updateSeason(
  id: string,
  draft: TSeasonDraft
): Promise<TSeason> {
  const res = await api.put<TSeasonReadWire>(
    `${base}/${id}`,
    draftToWire(draft)
  )
  return wireToSeason(res.data)
}

export async function publishSeason(id: string): Promise<TSeason> {
  const res = await api.post<TSeasonReadWire>(`${base}/${id}/publish`)
  return wireToSeason(res.data)
}

export async function deleteSeason(id: string): Promise<void> {
  await api.delete(`${base}/${id}`)
}

// Validate a saved season draft on the backend; returns the notice list
// (blockers refuse publish, warnings/info are advisory).
export async function preflightSeason(id: string): Promise<TSeasonNotice[]> {
  const res = await api.post<TSeasonNotice[]>(`${base}/${id}/preflight`)
  return res.data
}

// ---- participant curation (all return the refreshed season) ----------------

// Resolve a tag-mode season's eligible teams into the accepted pool (before publish).
export async function resolveSeasonTeams(id: string): Promise<TSeason> {
  const res = await api.post<TSeasonReadWire>(`${base}/${id}/resolve-teams`)
  return wireToSeason(res.data)
}

export async function addSeasonParticipants(
  id: string,
  teamIds: string[]
): Promise<TSeason> {
  const res = await api.post<TSeasonReadWire>(`${base}/${id}/participants`, {
    team_ids: teamIds,
  })
  return wireToSeason(res.data)
}

export async function acceptSeasonApplicants(
  id: string,
  teamIds: string[],
  reason?: string
): Promise<TSeason> {
  const res = await api.post<TSeasonReadWire>(
    `${base}/${id}/participants/accept`,
    { team_ids: teamIds, reason: reason?.trim() || undefined }
  )
  return wireToSeason(res.data)
}

export async function rejectSeasonApplicants(
  id: string,
  teamIds: string[],
  reason?: string
): Promise<TSeason> {
  const res = await api.post<TSeasonReadWire>(
    `${base}/${id}/participants/reject`,
    { team_ids: teamIds, reason: reason?.trim() || undefined }
  )
  return wireToSeason(res.data)
}

export async function removeSeasonParticipant(
  id: string,
  teamId: string
): Promise<TSeason> {
  const res = await api.delete<TSeasonReadWire>(
    `${base}/${id}/participants/${teamId}`
  )
  return wireToSeason(res.data)
}

// Manually instantiate (start) a pending period now.
export async function instantiateSeasonPeriod(
  periodId: string
): Promise<TSeason> {
  const res = await api.post<TSeasonReadWire>(
    `${base}/periods/${periodId}/instantiate`
  )
  return wireToSeason(res.data)
}

// Complete a finished period: evaluate conditions → seed the next period.
export async function completeSeasonPeriod(periodId: string): Promise<TSeason> {
  const res = await api.post<TSeasonReadWire>(
    `${base}/periods/${periodId}/complete`
  )
  return wireToSeason(res.data)
}

export async function getSeasonContext(): Promise<TSeasonContext> {
  const res = await api.get<TSeasonContext>(`${base}/context`)
  return res.data
}

export async function getSeasonApplicationRuleCatalog(): Promise<
  TSeasonApplicationRuleDefinition[]
> {
  const res = await api.get<TSeasonApplicationRuleDefinition[]>(
    `${base}/application-rules`
  )
  return res.data
}

export async function getSeasonApplications(
  id: string
): Promise<TSeasonAdminApplications> {
  const res = await api.get<TSeasonAdminApplications>(
    `${base}/${id}/applications`
  )
  return res.data
}

export async function getSeasonEligibleCount(
  query: TSeasonEligibleCountQuery
): Promise<TSeasonEligibleCount> {
  const res = await api.post<TSeasonEligibleCount>(
    `${base}/eligible-count`,
    query
  )
  return res.data
}

export async function getSeasonTeams(
  id: string,
  page = 1,
  per_page = 20,
  q?: string
): Promise<TSeasonTeamStatsResponse> {
  const params = new URLSearchParams()
  params.append('page', String(page))
  params.append('per_page', String(per_page))
  if (q) params.append('q', q)
  const res = await api.get<TSeasonTeamStatsResponse>(
    `${base}/${id}/teams?${params.toString()}`
  )
  return res.data
}

export async function getTeamSeasonMemberships(
  teamId: string
): Promise<TTeamSeasonMembership[]> {
  const res = await api.get<TTeamSeasonMembership[]>(
    `${base}/team/${teamId}/memberships`
  )
  return res.data
}
